import React from 'react';
import { Skeleton, SkeletonText } from '../../../components/ui/SkeletonLoader';

const PropertyCardSkeleton = () => (
  <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elevation-sm">
    {/* Image skeleton */}
    <Skeleton className="w-full h-48 rounded-none" />
    <div className="p-4 space-y-3">
      {/* Price */}
      <Skeleton className="h-7 w-2/5" />
      {/* Address */}
      <SkeletonText lines={2} />
      {/* Badges row */}
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
      {/* Description */}
      <SkeletonText lines={2} />
      {/* Action buttons */}
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-9 flex-1 rounded-lg" />
        <Skeleton className="h-9 w-9 rounded-lg" />
      </div>
    </div>
  </div>
);

export const SearchResultsSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
    {Array.from({ length: count })?.map((_, i) => (
      <PropertyCardSkeleton key={i} />
    ))}
  </div>
);

export const SearchResultsSpinner = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-4">
    <div className="relative">
      <div className="w-14 h-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full bg-primary/30 animate-pulse" />
      </div>
    </div>
    <div className="text-center">
      <p className="text-sm font-medium text-foreground">Searching properties...</p>
      <p className="text-xs text-muted-foreground mt-1">Finding the best matches for you</p>
    </div>
  </div>
);

export const SearchEmptyState = ({ query = '', onClearFilters }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div className="relative mb-6">
      <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
        <svg className="w-12 h-12 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803 7.5 7.5 0 0015.803 15.803z" />
        </svg>
      </div>
      <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
        <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    </div>
    <h3 className="text-xl font-semibold text-foreground mb-2">
      No properties found
    </h3>
    <p className="text-sm text-muted-foreground max-w-sm mb-2">
      {query
        ? <>No results for <span className="font-medium text-foreground">"{query}"</span>. Try a different suburb, postcode, or address.</>
        : 'No properties match your current filters. Try broadening your search criteria.'}
    </p>
    <p className="text-xs text-muted-foreground mb-6">Tip: Try removing some filters or searching a nearby area</p>
    <div className="flex flex-col sm:flex-row gap-3">
      {onClearFilters && (
        <button
          onClick={onClearFilters}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Clear All Filters
        </button>
      )}
      <button
        onClick={() => window.history?.back()}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Go Back
      </button>
    </div>
  </div>
);

export default PropertyCardSkeleton;
